import { WasteManifestSource } from "generated-graphql/graphql";

import { DOCUMENT_CHIP_HEX } from "./sourceColors";

/**
 * Canonical display order for the three manifest data pillars. The document
 * (the signed paper of record) outranks the EPA feed and vendor data; note
 * the backend only includes a document in `availableSources` once its
 * extracted data has been human-reviewed — an unreviewed document is not a
 * source. Mirrors MANIFEST_SOURCE_DISPLAY_PRIORITY in encamp-shared.
 */
export const SOURCE_ORDER: readonly WasteManifestSource[] = [
  WasteManifestSource.ManifestDocument,
  WasteManifestSource.EManifest,
  WasteManifestSource.Vendor,
];

export const SOURCE_LABEL: Record<WasteManifestSource, string> = {
  [WasteManifestSource.EManifest]: "e-Manifest",
  [WasteManifestSource.Vendor]: "Vendor",
  [WasteManifestSource.ManifestDocument]: "Uploaded Manifest Document",
};

/** Caption name for the primary source; an uploaded bill of lading is not a "Manifest Document". */
export function primarySourceLabel(source: WasteManifestSource, isBillOfLading: boolean): string {
  if (isBillOfLading && source === WasteManifestSource.ManifestDocument) {
    return "Uploaded bill of lading";
  }
  return SOURCE_LABEL[source];
}

/**
 * Compact labels for space-constrained chips (e.g. the narrow Data Sources
 * column in the manifest drawer), where the full "Uploaded Manifest Document"
 * overflows. Only the document source needs shortening; the info-tooltip
 * explainer still shows the full {@link SOURCE_LABEL} so "Upload" stays clear.
 */
export const SOURCE_LABEL_SHORT: Record<WasteManifestSource, string> = {
  [WasteManifestSource.EManifest]: "e-Manifest",
  [WasteManifestSource.Vendor]: "Vendor",
  [WasteManifestSource.ManifestDocument]: "Upload",
};

/** Labels for lists that hold both manifests and bills, where "Manifest Document" would mislabel a bill. */
export const MIXED_LIST_SOURCE_LABEL: Record<WasteManifestSource, string> = {
  ...SOURCE_LABEL,
  [WasteManifestSource.ManifestDocument]: "Uploaded document",
};

/**
 * `MIXED_LIST_SOURCE_LABEL` keyed by the raw omnisearch string value, for the
 * sources filter's `enumPresentationFunction`, which receives a plain `string`
 * rather than the `WasteManifestSource` enum.
 */
export const MIXED_LIST_SOURCE_LABEL_BY_VALUE = new Map<string, string>(
  Object.entries(MIXED_LIST_SOURCE_LABEL)
);

/**
 * The "Document" source accent: a violet-blue (indigo[500]), high-contrast as
 * a chip label. Deliberately NOT orange — orange is reserved across the app
 * for staff-only affordances (e.g. the edit pencil). Read via
 * `getSourceAccent` in SourceLetterBox.tsx; value lives in
 * {@link DOCUMENT_CHIP_HEX} (shared with the M3 compare dialog).
 */
export const DOCUMENT_SOURCE_COLOR = DOCUMENT_CHIP_HEX;

/**
 * The data-source tab a manifest opens on. There is no user-facing "primary"
 * concept: we default to the manifest's `primarySource` when it's available,
 * then the first available source, then e-Manifest — but the user only ever
 * sees and selects concrete sources.
 */
export function defaultSource(
  primarySource: WasteManifestSource | null | undefined,
  availableSources: readonly WasteManifestSource[]
): WasteManifestSource {
  if (primarySource && availableSources.includes(primarySource)) return primarySource;
  const firstAvailable = SOURCE_ORDER.find((s) => availableSources.includes(s));
  return firstAvailable ?? availableSources[0] ?? WasteManifestSource.EManifest;
}

export interface ResolvedFieldValue {
  value: string | null | undefined;
  /** False when the selected source carries no value for this field. */
  present: boolean;
}

/**
 * Pick a field's value for a concrete source from a per-source value map. A
 * source is "present" only when it is in `availableSources` AND has a value.
 */
export function pickBySource(
  source: WasteManifestSource,
  availableSources: readonly WasteManifestSource[],
  values: Partial<Record<WasteManifestSource, string | null | undefined>>
): ResolvedFieldValue {
  const value = values[source];
  const present = availableSources.includes(source) && value != null && value !== "";
  return { value: present ? value : undefined, present };
}

export interface ResolvedFieldValueWithSource extends ResolvedFieldValue {
  /** The source the value came from, or null when no source had a value. */
  source: WasteManifestSource | null;
}

/**
 * Resolve a field's displayed value by canonical priority: try `primary` first,
 * then the rest of {@link SOURCE_ORDER}, returning the first source that is
 * available AND has a value. The single home for the primary-then-fallback rule
 * — shared by `SourcedField` (value + the fallback dot's source) and the sticky
 * header, so the two can't drift.
 */
export function resolveByPriority(
  primary: WasteManifestSource | null | undefined,
  availableSources: readonly WasteManifestSource[],
  values: Partial<Record<WasteManifestSource, string | null | undefined>>
): ResolvedFieldValueWithSource {
  const order = primary ? [primary, ...SOURCE_ORDER.filter((s) => s !== primary)] : SOURCE_ORDER;
  for (const candidate of order) {
    const resolved = pickBySource(candidate, availableSources, values);
    if (resolved.present) return { ...resolved, source: candidate };
  }
  return { value: undefined, present: false, source: null };
}

/** Value-only convenience over {@link resolveByPriority}. */
export function pickByPriority(
  primary: WasteManifestSource | null | undefined,
  availableSources: readonly WasteManifestSource[],
  values: Partial<Record<WasteManifestSource, string | null | undefined>>
): string | null | undefined {
  return resolveByPriority(primary, availableSources, values).value;
}
